import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { BRAND } from "@/lib/data";

const SECTIONS = [
  {
    id: "titolare",
    number: "01",
    title: "Titolare del Trattamento",
    body: [
      "Il Titolare del trattamento dei dati personali è Francesca Cicardi, Massoterapista M.C.B., con studio in Via Monte Carso, 19 — 20010 Vanzago (MI), contattabile al numero 348 312 3462 o all'indirizzo email cicardi.francesca@gmail.com.",
      "L'attività è svolta come prestazione di lavoro autonomo ai sensi dell'art. 2222 del Codice Civile.",
    ],
  },
  {
    id: "dati-raccolti",
    number: "02",
    title: "Dati Personali Raccolti",
    body: [
      "Attraverso il modulo contatti presente su questo sito vengono raccolti esclusivamente i dati che l'utente inserisce volontariamente: nome e cognome, numero di telefono e contenuto del messaggio.",
      "Vengono inoltre trattati i dati di navigazione tecnici strettamente necessari al funzionamento del sito (indirizzo IP, tipo di browser, orario di accesso), raccolti in forma anonimizzata dai sistemi informatici.",
    ],
  },
  {
    id: "finalita",
    number: "03",
    title: "Finalità e Base Giuridica",
    body: [
      "I dati forniti tramite il modulo contatti sono trattati al solo scopo di rispondere alle richieste di informazioni o di prenotazione inviate dall'utente (art. 6, par. 1, lett. b del GDPR — esecuzione di misure precontrattuali).",
      "I dati non vengono utilizzati per finalità di marketing, profilazione o invio di comunicazioni promozionali senza un esplicito consenso.",
    ],
  },
  {
    id: "conservazione",
    number: "04",
    title: "Modalità di Trattamento e Conservazione",
    body: [
      "Il trattamento avviene con strumenti informatici e con misure di sicurezza adeguate a garantire riservatezza e integrità dei dati.",
      "I messaggi ricevuti tramite il modulo contatti sono conservati per il tempo strettamente necessario a gestire la richiesta e, in ogni caso, non oltre 12 mesi, salvo diversi obblighi di legge. Decorso tale termine, i dati vengono cancellati.",
    ],
  },
  {
    id: "terzi",
    number: "05",
    title: "Comunicazione a Terzi",
    body: [
      "I dati personali non sono mai venduti, ceduti o diffusi a terzi. Possono essere trattati, per sole finalità tecniche, dai fornitori dei servizi di hosting e infrastruttura che ospitano questo sito, nominati responsabili del trattamento ove applicabile.",
    ],
  },
  {
    id: "diritti",
    number: "06",
    title: "Diritti dell'Interessato",
    body: [
      "In qualità di interessato, l'utente può esercitare in qualsiasi momento i diritti previsti dagli artt. 15-22 del GDPR: accesso ai dati, rettifica, cancellazione (diritto all'oblio), limitazione e opposizione al trattamento, portabilità dei dati.",
      "Per esercitare i propri diritti è sufficiente contattare il Titolare al numero 348 312 3462 o via email all'indirizzo cicardi.francesca@gmail.com. È inoltre possibile proporre reclamo all'Autorità Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
];

const COOKIES = [
  {
    name: "Cookie tecnici",
    description: "Strettamente necessari al funzionamento e alla sicurezza del sito. Non richiedono consenso e non vengono utilizzati per profilazione.",
  },
  {
    name: "Google Fonts",
    description: "I caratteri tipografici sono caricati dai server di Google. La connessione può comportare la trasmissione dell'indirizzo IP a Google LLC (USA). Privacy policy: policies.google.com/privacy.",
  },
  {
    name: "Google Maps",
    description: "La mappa interattiva incorporata nella sezione contatti è fornita da Google. Interagendo con la mappa, Google può installare cookie di terze parti secondo la propria informativa.",
  },
  {
    name: "WhatsApp",
    description: "I pulsanti di prenotazione rimandano a WhatsApp (Meta Platforms, Inc.). Il trattamento dei dati su tale piattaforma è regolato dalla relativa informativa privacy.",
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <main data-testid="privacy-page" className="bg-[#FAF8F5] min-h-screen">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo" className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#489A88] text-white flex items-center justify-center font-['Outfit'] font-semibold text-sm">
              FC
            </span>
            <span className="leading-tight">
              <span className="block font-['Outfit'] font-medium text-[#1A2E2A] text-base">Francesca Cicardi</span>
              <span className="block text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Massoterapia · Vanzago</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white text-sm font-medium text-[#1A2E2A] px-5 py-2.5 hover:border-[#489A88] hover:text-[#2D6D60] transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-20 lg:py-28">
        <Reveal>
          <Overline>Documento Legale</Overline>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A2E2A] leading-[1.1]">
            Privacy e <span className="italic text-[#489A88]">Cookie Policy</span>
          </h1>
          <p className="mt-6 flex items-center gap-2.5 text-sm text-[#64748B]">
            <ShieldCheck className="w-4 h-4 text-[#489A88]" />
            Informativa resa ai sensi del Regolamento (UE) 2016/679 (GDPR) — Ultimo aggiornamento: Luglio 2026
          </p>
        </Reveal>

        <div className="mt-16 space-y-4">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}>
              <section data-testid={`privacy-section-${s.id}`} className="rounded-[1.5rem] bg-white border border-[#E2E8F0]/80 p-8 sm:p-10">
                <div className="flex items-baseline gap-5">
                  <span className="font-['Outfit'] text-sm font-medium text-[#489A88] tracking-widest">{s.number}</span>
                  <div>
                    <h2 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-[#1A2E2A]">{s.title}</h2>
                    {s.body.map((p, j) => (
                      <p key={j} className="mt-4 text-sm sm:text-base leading-relaxed text-[#4A5568]">{p}</p>
                    ))}
                  </div>
                </div>
              </section>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <section data-testid="privacy-section-cookie" className="rounded-[1.5rem] bg-white border border-[#E2E8F0]/80 p-8 sm:p-10">
              <div className="flex items-baseline gap-5">
                <span className="font-['Outfit'] text-sm font-medium text-[#489A88] tracking-widest">07</span>
                <div className="flex-1">
                  <h2 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-[#1A2E2A]">Cookie Policy</h2>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#4A5568]">
                    Questo sito non utilizza cookie propri di profilazione né strumenti di tracciamento
                    pubblicitario. Di seguito i servizi di terze parti che possono comportare il rilascio
                    di cookie o la trasmissione di dati tecnici:
                  </p>
                  <div className="mt-6 space-y-3">
                    {COOKIES.map((c) => (
                      <div key={c.name} className="rounded-xl bg-[#FAF8F5] border border-[#E2E8F0]/70 px-5 py-4">
                        <p className="text-sm font-semibold text-[#1A2E2A]">{c.name}</p>
                        <p className="mt-1 text-sm leading-relaxed text-[#64748B]">{c.description}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-sm leading-relaxed text-[#4A5568]">
                    L'utente può gestire o eliminare i cookie in qualsiasi momento tramite le impostazioni
                    del proprio browser.
                  </p>
                </div>
              </div>
            </section>
          </Reveal>

          <Reveal delay={0.12}>
            <section data-testid="privacy-section-modifiche" className="rounded-[1.5rem] bg-white border border-[#E2E8F0]/80 p-8 sm:p-10">
              <div className="flex items-baseline gap-5">
                <span className="font-['Outfit'] text-sm font-medium text-[#489A88] tracking-widest">08</span>
                <div>
                  <h2 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-[#1A2E2A]">Modifiche alla Presente Informativa</h2>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#4A5568]">
                    Il Titolare si riserva di aggiornare la presente informativa in qualsiasi momento.
                    La versione vigente è quella pubblicata su questa pagina, con indicazione della data
                    di ultimo aggiornamento.
                  </p>
                </div>
              </div>
            </section>
          </Reveal>
        </div>
      </div>

      <footer className="bg-[#1A2E2A] text-white">
        <div className="max-w-4xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4">
          <p className="text-xs text-white/45 max-w-xl leading-relaxed">{BRAND.legal}</p>
          <p className="text-xs text-white/45">
            © {new Date().getFullYear()} Francesca Cicardi — Massoterapia
          </p>
        </div>
      </footer>
    </main>
  );
}
